import React, {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import styles from './Combobox.module.css';

/* ── Types ── */

export type ComboboxSize   = 'sm' | 'md' | 'lg';
export type ComboboxStatus = 'default' | 'error' | 'success';

export interface ComboboxOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface ComboboxProps {
  options: ComboboxOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onCreate?: (value: string) => void;
  placeholder?: string;
  label?: string;
  hint?: string;
  size?: ComboboxSize;
  status?: ComboboxStatus;
  disabled?: boolean;
  fullWidth?: boolean;
  id?: string;
  className?: string;
}

export interface ComboboxHandle {
  focus: () => void;
}

/* ── Helpers ── */

const cx = (...classes: (string | false | null | undefined)[]) =>
  classes.filter(Boolean).join(' ');

/* ── Chevron icon ── */

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    className={cx(styles.chevron, open && styles.chevronOpen)}
    aria-hidden="true"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M4 6l4 4 4-4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ── Check icon ── */

const Check = () => (
  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M2.5 7l3.5 3.5 5.5-6"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ── Component ── */

const Combobox = forwardRef<ComboboxHandle, ComboboxProps>(
  (
    {
      options,
      value: controlledValue,
      defaultValue = '',
      onChange,
      onCreate,
      placeholder = 'Select or type…',
      label,
      hint,
      size = 'md',
      status = 'default',
      disabled = false,
      fullWidth = false,
      id: externalId,
      className,
    },
    ref,
  ) => {
    const autoId = useId();
    const id = externalId ?? autoId;
    const labelId = `${id}-label`;
    const listId  = `${id}-listbox`;

    const isControlled = controlledValue !== undefined;
    const [internalValue, setInternalValue] = useState(defaultValue);
    const value = isControlled ? controlledValue : internalValue;

    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(-1);

    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef  = useRef<HTMLUListElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (!disabled) {
          setOpen(true);
          inputRef.current?.focus();
        }
      },
    }));

    const selectedOption = options.find(o => o.value === value);

    useEffect(() => {
      if (selectedOption) {
        setInputValue(selectedOption.label);
      } else if (!value) {
        setInputValue('');
      }
    }, [selectedOption, value]);

    const filteredOptions = options.filter(o =>
      o.label.toLowerCase().includes(inputValue.toLowerCase())
    );

    const showCreateOption = onCreate && inputValue.trim() !== '' &&
      !options.some(o => o.label.toLowerCase() === inputValue.toLowerCase());

    /* ── Close on outside click ── */
    useEffect(() => {
      if (!open) return;
      const handler = (e: MouseEvent) => {
        if (!containerRef.current?.contains(e.target as Node)) {
          setOpen(false);
          setFocusedIndex(-1);
          // Restore input value if no selection made
          if (selectedOption) {
            setInputValue(selectedOption.label);
          } else if (!value) {
            setInputValue('');
          }
        }
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, [open, selectedOption, value]);

    /* ── Scroll focused option into view ── */
    useEffect(() => {
      if (!open || focusedIndex < 0) return;
      const item = dropdownRef.current?.children[focusedIndex] as HTMLElement | undefined;
      item?.scrollIntoView({ block: 'nearest' });
    }, [focusedIndex, open]);

    const select = (opt: ComboboxOption) => {
      if (opt.disabled) return;
      if (!isControlled) setInternalValue(opt.value);
      onChange?.(opt.value);
      setInputValue(opt.label);
      setOpen(false);
      setFocusedIndex(-1);
    };

    const handleCreate = () => {
      onCreate?.(inputValue);
      setOpen(false);
      setFocusedIndex(-1);
    };

    const handleInputKeyDown = (e: React.KeyboardEvent) => {
      if (disabled) return;
      const totalOptions = filteredOptions.length + (showCreateOption ? 1 : 0);

      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          if (open) {
            if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
              select(filteredOptions[focusedIndex]);
            } else if (focusedIndex === filteredOptions.length && showCreateOption) {
              handleCreate();
            } else if (filteredOptions.length === 1 && !showCreateOption) {
              select(filteredOptions[0]);
            } else if (showCreateOption && filteredOptions.length === 0) {
              handleCreate();
            }
          } else {
            setOpen(true);
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (!open) { setOpen(true); setFocusedIndex(0); break; }
          setFocusedIndex(i => {
            const next = i + 1;
            return next < totalOptions ? next : i;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!open) { setOpen(true); break; }
          setFocusedIndex(i => {
            const prev = i - 1;
            return prev >= 0 ? prev : i;
          });
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          setFocusedIndex(-1);
          if (selectedOption) setInputValue(selectedOption.label);
          break;
        case 'Tab':
          setOpen(false);
          setFocusedIndex(-1);
          break;
      }
    };

    return (
      <div
        className={cx(styles.root, fullWidth && styles.fullWidth, className)}
      >
        {label && (
          <label id={labelId} htmlFor={id} className={styles.label}>
            {label}
          </label>
        )}

        <div ref={containerRef} className={cx(styles.container, fullWidth && styles.fullWidth)}>
          {/* ── Trigger / Input ── */}
          <div
            className={cx(
              styles.inputWrapper,
              styles[size],
              status !== 'default' && styles[status],
              open && styles.inputWrapperOpen,
              disabled && styles.disabled
            )}
            onClick={() => { if (!disabled) { setOpen(true); inputRef.current?.focus(); } }}
          >
            <input
              ref={inputRef}
              id={id}
              type="text"
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded={open}
              aria-controls={listId}
              aria-labelledby={label ? labelId : undefined}
              aria-disabled={disabled}
              disabled={disabled}
              autoComplete="off"
              className={styles.input}
              placeholder={placeholder}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setOpen(true);
                setFocusedIndex(-1);
              }}
              onKeyDown={handleInputKeyDown}
            />
            <Chevron open={open} />
          </div>

          {/* ── Dropdown ── */}
          {open && (filteredOptions.length > 0 || showCreateOption) && (
            <ul
              ref={dropdownRef}
              id={listId}
              role="listbox"
              aria-label={label}
              className={styles.dropdown}
            >
              {filteredOptions.map((opt, idx) => (
                <li
                  key={opt.value}
                  role="option"
                  aria-selected={opt.value === value}
                  aria-disabled={opt.disabled}
                  className={cx(
                    styles.option,
                    opt.value === value && styles.optionSelected,
                    idx === focusedIndex && styles.optionFocused,
                    opt.disabled && styles.optionDisabled,
                  )}
                  onMouseEnter={() => !opt.disabled && setFocusedIndex(idx)}
                  onMouseDown={e => { e.preventDefault(); select(opt); }}
                >
                  <span className={styles.optionLabel}>{opt.label}</span>
                  {opt.value === value && (
                    <span className={styles.optionCheck}><Check /></span>
                  )}
                </li>
              ))}
              {showCreateOption && (
                <li
                  role="option"
                  className={cx(
                    styles.option,
                    styles.createOption,
                    focusedIndex === filteredOptions.length && styles.optionFocused
                  )}
                  onMouseEnter={() => setFocusedIndex(filteredOptions.length)}
                  onMouseDown={e => { e.preventDefault(); handleCreate(); }}
                >
                  <span className={styles.optionLabel}>+ Create new well "{inputValue}"</span>
                </li>
              )}
            </ul>
          )}
        </div>

        {hint && (
          <span className={cx(styles.hint, status !== 'default' && styles[status])}>
            {hint}
          </span>
        )}
      </div>
    );
  },
);

Combobox.displayName = 'Combobox';

export { Combobox };
